import {
  Background,
  Controls,
  type Edge,
  MarkerType,
  type Node,
  type NodeMouseHandler,
  ReactFlow,
} from "@xyflow/react";
import { useMemo } from "react";
import "@xyflow/react/dist/style.css";
import { layoutGraph } from "./dagreLayout";
import { AssessmentGuideNode } from "./nodes/AssessmentGuideNode";
import { AssessmentMatrixNode } from "./nodes/AssessmentMatrixNode";
import { CampaignBriefNode } from "./nodes/CampaignBriefNode";
import { CampaignJobPostingNode } from "./nodes/CampaignJobPostingNode";
import { CampaignNode } from "./nodes/CampaignNode";
import { CandidateAssessmentsAggregateNode } from "./nodes/CandidateAssessmentsAggregateNode";
import { CandidateScoringMatrixNode } from "./nodes/CandidateScoringMatrixNode";
import { InterviewGuideNode } from "./nodes/InterviewGuideNode";
import { RoleBlueprintNode } from "./nodes/RoleBlueprintNode";
import { ScreeningCriteriaNode } from "./nodes/ScreeningCriteriaNode";
import type { ArtefactKind, LineageEdge, LineageGraph, LineageNode, OpenArtefact } from "./types";

const nodeTypes = {
  campaign: CampaignNode,
  role_blueprint: RoleBlueprintNode,
  campaign_brief: CampaignBriefNode,
  screening_criteria: ScreeningCriteriaNode,
  assessment_matrix: AssessmentMatrixNode,
  candidate_scoring_matrix: CandidateScoringMatrixNode,
  interview_guide: InterviewGuideNode,
  assessment_guide: AssessmentGuideNode,
  campaign_job_posting: CampaignJobPostingNode,
  candidate_assessments_aggregate: CandidateAssessmentsAggregateNode,
};

const NODE_KIND_TO_ARTEFACT_KIND: Partial<Record<LineageNode["kind"], ArtefactKind>> = {
  role_blueprint: "role_blueprint",
  campaign_brief: "campaign_brief",
  screening_criteria: "screening_criteria",
  assessment_matrix: "assessment_matrix",
  candidate_scoring_matrix: "candidate_scoring_matrix",
  interview_guide: "interview_guide",
  assessment_guide: "assessment_guide",
  campaign_job_posting: "campaign_job_posting",
};

function styleForEdge(
  edge: LineageEdge,
): Pick<Edge, "type" | "animated" | "style" | "markerEnd" | "label" | "labelStyle"> {
  if (edge.kind === "contains") {
    return {
      type: "smoothstep",
      animated: false,
      style: { stroke: "#94a3b8", strokeWidth: 1.25, strokeDasharray: "5 5", opacity: 0.7 },
    };
  }
  if (edge.kind === "child") {
    return {
      type: "smoothstep",
      animated: false,
      style: { stroke: "var(--color-border-strong, #94a3b8)", strokeWidth: 1.5 },
      markerEnd: { type: MarkerType.ArrowClosed, color: "var(--color-border-strong, #94a3b8)" },
    };
  }
  // pin edge
  if (edge.state === "stale") {
    return {
      type: "smoothstep",
      animated: false,
      style: { stroke: "#dc2626", strokeWidth: 2 },
      markerEnd: { type: MarkerType.ArrowClosed, color: "#dc2626" },
      label:
        edge.pinnedVersionNumber !== null && edge.currentBlueprintVersionNumber !== null
          ? `v${edge.pinnedVersionNumber} ← v${edge.currentBlueprintVersionNumber}`
          : undefined,
      labelStyle: { fontSize: 11, fill: "#dc2626" },
    };
  }
  if (edge.state === "not_applicable") {
    // The artefact exists but its current version was created without a
    // blueprint pin — draw a faint dashed dependency edge so the link isn't
    // missing entirely, but don't suggest it's current or stale.
    return {
      type: "smoothstep",
      animated: false,
      style: { stroke: "#94a3b8", strokeWidth: 1.25, strokeDasharray: "3 4", opacity: 0.6 },
      markerEnd: { type: MarkerType.ArrowClosed, color: "#94a3b8" },
      label: "unpinned",
      labelStyle: { fontSize: 10, fill: "#94a3b8", fontStyle: "italic" },
    };
  }
  return {
    type: "smoothstep",
    animated: false,
    style: { stroke: "#d97706", strokeWidth: 1.5 },
    markerEnd: { type: MarkerType.ArrowClosed, color: "#d97706" },
  };
}

interface CampaignLineageGraphProps {
  lineage: LineageGraph;
  selectedNodeId: string | null;
  onOpenHistory: (open: OpenArtefact) => void;
}

export function CampaignLineageGraph({
  lineage,
  selectedNodeId,
  onOpenHistory,
}: CampaignLineageGraphProps) {
  const { nodes, edges } = useMemo(() => {
    const flowNodes: Node[] = lineage.nodes.map((n) => ({
      id: n.id,
      type: n.kind,
      data: n as unknown as Record<string, unknown>,
      position: { x: 0, y: 0 },
      selected: n.id === selectedNodeId,
    }));
    const flowEdges: Edge[] = lineage.edges.map((e) => ({
      id: e.id,
      source: e.from,
      target: e.to,
      ...styleForEdge(e),
    }));
    return { nodes: layoutGraph(flowNodes, flowEdges), edges: flowEdges };
  }, [lineage, selectedNodeId]);

  const handleNodeClick: NodeMouseHandler = (_event, node) => {
    const data = node.data as unknown as LineageNode;
    const artefactKind = NODE_KIND_TO_ARTEFACT_KIND[data.kind];
    if (!artefactKind || !data.artefactId) return;
    onOpenHistory({
      nodeId: data.id,
      kind: artefactKind,
      artefactId: data.artefactId,
      label: data.label,
    });
  };

  return (
    <div className="h-[680px] w-full rounded-lg border border-border bg-surface">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        onNodeClick={handleNodeClick}
        fitView
        nodesDraggable={false}
        nodesConnectable={false}
        edgesReconnectable={false}
        elementsSelectable
        panOnDrag
        proOptions={{ hideAttribution: true }}
      >
        <Background gap={20} />
        <Controls showInteractive={false} />
      </ReactFlow>
    </div>
  );
}
